/**
 * New group flow (06 S11 → S12). Step 1: member multi-select with a chips rail.
 * Step 2: subject + (stub) icon, then create.
 */
import { useState } from 'react';
import { ArrowRight, Camera, Check, X } from 'lucide-react';
import { useUiStore } from '../../stores/uiStore';
import { USERS } from '../../data/mockData';
import { OverlayPanel } from '../primitives/OverlayPanel';
import { ContactPickerRow } from './ContactPickerRow';
import { Avatar } from '../primitives/Avatar';
import styles from './NewGroup.module.css';

export function NewGroupMembers({ selected }: { selected: string[] }) {
  const setLeftOverlay = useUiStore((s) => s.setLeftOverlay);
  const closeLeftOverlay = useUiStore((s) => s.closeLeftOverlay);
  const [picked, setPicked] = useState<string[]>(selected);

  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <OverlayPanel
      title="Add group members"
      subtitle={`${picked.length} of ${USERS.length} selected`}
      onBack={closeLeftOverlay}
    >
      {picked.length > 0 && (
        <div className={styles.chips}>
          {picked.map((id) => {
            const u = USERS.find((x) => x.id === id)!;
            return (
              <button key={id} className={styles.chip} onClick={() => toggle(id)}>
                <Avatar id={u.id} name={u.displayName} size={28} />
                <span>{u.displayName.split(' ')[0]}</span>
                <X size={13} />
              </button>
            );
          })}
        </div>
      )}
      <ul>
        {USERS.map((u) => (
          <li key={u.id}>
            <ContactPickerRow
              user={u}
              multiselect
              selected={picked.includes(u.id)}
              onClick={() => toggle(u.id)}
            />
          </li>
        ))}
      </ul>
      {picked.length > 0 && (
        <button
          className={styles.next}
          onClick={() => setLeftOverlay({ kind: 'new-group-details', selected: picked })}
          aria-label="Next"
        >
          <ArrowRight size={24} />
        </button>
      )}
    </OverlayPanel>
  );
}

export function NewGroupDetails({ selected }: { selected: string[] }) {
  const setLeftOverlay = useUiStore((s) => s.setLeftOverlay);
  const closeLeftOverlay = useUiStore((s) => s.closeLeftOverlay);
  const toast = useUiStore((s) => s.toast);
  const [subject, setSubject] = useState('');

  const create = () => {
    if (!subject.trim()) return;
    // In the live app this calls restClient.createConversation({type:'group',...})
    // then navigates. Backendless we acknowledge and close.
    toast(`Group “${subject.trim()}” created (demo)`);
    closeLeftOverlay();
  };

  return (
    <OverlayPanel
      title="New group"
      onBack={() => setLeftOverlay({ kind: 'new-group-members', selected })}
    >
      <div className={styles.subjectArea}>
        <button className={styles.iconUpload} aria-label="Add group icon">
          <Camera size={26} />
        </button>
        <input
          className={styles.subject}
          autoFocus
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Group subject"
          aria-label="Group subject"
        />
      </div>
      <h2 className={styles.section}>Members: {selected.length}</h2>
      <ul className={styles.members}>
        {selected.map((id) => {
          const u = USERS.find((x) => x.id === id)!;
          return (
            <li key={id} className={styles.member}>
              <Avatar id={u.id} name={u.displayName} size={32} />
              <span>{u.displayName}</span>
            </li>
          );
        })}
      </ul>
      <button className={styles.create} onClick={create} disabled={!subject.trim()} aria-label="Create group">
        <Check size={24} />
      </button>
    </OverlayPanel>
  );
}
